import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { reportsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtShort = (n) => Number(n||0).toLocaleString('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0});

export default function SalesReport() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth()-1); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('day');

  const load = async () => {
    setLoading(true);
    try {
      const r = await reportsAPI.salesSummary({ date_from: dateFrom, date_to: dateTo, group_by: groupBy });
      setData(r.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const chartData = (data || []).map(d => ({
    period: new Date(d.period).toLocaleDateString('en-IN', groupBy === 'month' ? {month:'short',year:'2-digit'} : groupBy === 'week' ? {day:'numeric',month:'short'} : {day:'numeric',month:'short'}),
    sales: Number(d.total_sales),
    collected: Number(d.collected),
    gst: Number(d.total_gst)
  }));

  const totals = (data || []).reduce((acc, d) => ({
    invoices: acc.invoices + Number(d.invoice_count),
    sales: acc.sales + Number(d.total_sales),
    taxable: acc.taxable + Number(d.taxable_amount),
    gst: acc.gst + Number(d.total_gst),
    collected: acc.collected + Number(d.collected),
    outstanding: acc.outstanding + Number(d.outstanding),
  }), { invoices:0, sales:0, taxable:0, gst:0, collected:0, outstanding:0 });

  return (
    <div>
      <div className="page-header"><div><h1>Sales Report</h1><p className="subtitle">Sales analysis and trends</p></div></div>
      <div className="card mb-4"><div className="card-body">
        <div className="flex gap-3 items-center" style={{flexWrap:'wrap'}}>
          <div className="form-group" style={{marginBottom:0}}><label className="form-label">From</label><input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
          <div className="form-group" style={{marginBottom:0}}><label className="form-label">To</label><input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
          <div className="form-group" style={{marginBottom:0}}><label className="form-label">Group By</label>
            <select className="form-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
            </select></div>
          <div style={{paddingTop:20}}><button onClick={load} className="btn btn-primary" disabled={loading}>{loading ? 'Loading...' : 'Generate'}</button></div>
        </div>
      </div></div>

      {data && (
        <>
          <div className="stats-grid" style={{gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))'}}>
            <div className="stat-card"><div className="stat-info"><h3>Total Invoices</h3><div className="stat-value" style={{fontSize:22}}>{totals.invoices}</div></div></div>
            <div className="stat-card"><div className="stat-info"><h3>Total Sales</h3><div className="stat-value" style={{fontSize:22}}>{fmtShort(totals.sales)}</div></div></div>
            <div className="stat-card"><div className="stat-info"><h3>GST Collected</h3><div className="stat-value" style={{fontSize:22}}>{fmtShort(totals.gst)}</div></div></div>
            <div className="stat-card"><div className="stat-info"><h3>Collected</h3><div className="stat-value text-success" style={{fontSize:22}}>{fmtShort(totals.collected)}</div></div></div>
            <div className="stat-card"><div className="stat-info"><h3>Outstanding</h3><div className="stat-value text-danger" style={{fontSize:22}}>{fmtShort(totals.outstanding)}</div></div></div>
          </div>

          <div className="card mb-4">
            <div className="card-header"><h2>Sales Trend</h2></div>
            <div className="card-body" style={{height:350}}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis dataKey="period" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => fmtShort(v)} />
                    <Legend />
                    <Bar dataKey="sales" name="Total Sales" fill="#198754" radius={[4,4,0,0]} />
                    <Bar dataKey="collected" name="Collected" fill="#0d6efd" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="empty-state"><p>No data for selected period</p></div>}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2>Period Breakdown</h2></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Period</th><th style={{textAlign:'right'}}>Invoices</th><th style={{textAlign:'right'}}>Total Sales</th><th style={{textAlign:'right'}}>Taxable</th><th style={{textAlign:'right'}}>GST</th><th style={{textAlign:'right'}}>Collected</th><th style={{textAlign:'right'}}>Outstanding</th></tr></thead>
                <tbody>
                  {data.map((d,i) => (
                    <tr key={i}>
                      <td className="font-semibold">{new Date(d.period).toLocaleDateString('en-IN')}</td>
                      <td className="td-amount">{d.invoice_count}</td>
                      <td className="td-amount font-bold">₹{fmt(d.total_sales)}</td>
                      <td className="td-amount">₹{fmt(d.taxable_amount)}</td>
                      <td className="td-amount">₹{fmt(d.total_gst)}</td>
                      <td className="td-amount text-success">₹{fmt(d.collected)}</td>
                      <td className="td-amount text-danger">₹{fmt(d.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr style={{background:'#f8f9fa',fontWeight:700}}>
                  <td>Total</td><td className="td-amount">{totals.invoices}</td>
                  <td className="td-amount">₹{fmt(totals.sales)}</td><td className="td-amount">₹{fmt(totals.taxable)}</td>
                  <td className="td-amount">₹{fmt(totals.gst)}</td><td className="td-amount text-success">₹{fmt(totals.collected)}</td>
                  <td className="td-amount text-danger">₹{fmt(totals.outstanding)}</td>
                </tr></tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="card"><div className="empty-state" style={{padding:60}}><div className="empty-icon">📈</div><h3>Select date range and generate</h3></div></div>
      )}
    </div>
  );
}
